
import { GoogleBook, GoogleBooksApiResponse } from "./types";
import { googleBooksCache } from "./cache";
import { transformGoogleBookData } from "./transformer";
import { simplifyBookQuery, isWorthSimplifying } from "./query";
import { supabase } from "@/integrations/supabase/client";

// Google Books is now reached through our own edge function
// (google-books-search) so the request carries a server-side API key and no
// longer shares Google's burst-sensitive per-IP anonymous quota — the source of
// the 429s that left covers missing. REQUEST_TIMEOUT still bounds how long a
// single lookup may hang before we give up and let another source fill the cover.
const REQUEST_TIMEOUT = 8000; // 8 seconds for Apple platforms
// Direct Google endpoint, kept only as a fallback for when the proxy is
// unavailable (not yet deployed, key not set, transient error) so covers never
// regress from the previous direct-only behaviour.
const BASE_URL = "https://www.googleapis.com/books/v1/volumes";

// Google Books' anonymous quota is per-IP and burst-sensitive: it objects to
// several requests arriving at once, not to a steady trickle.
//
// This replaces a limiter that counted requests in a rolling ten-second window
// and dropped anything over the line. It had two problems. Ten lookups fired in
// the same tick all passed the count — none had been recorded yet — so they
// reached Google together and the tail came back HTTP 429. And anything beyond
// the tenth was discarded with an empty result, which is a permanently missing
// cover and no error to explain it.
//
// So: queue rather than drop, with a few requests in flight and a beat between
// starts. Every caller still gets an answer, just a little later.
const MAX_CONCURRENT = 3;
const MIN_SPACING_MS = 120;

let inFlight = 0;
let lastStartedAt = 0;
const waiting: Array<() => void> = [];

/** Admit queued callers up to the concurrency limit, claiming the slot for them. */
const pump = () => {
  while (inFlight < MAX_CONCURRENT && waiting.length > 0) {
    inFlight++;
    waiting.shift()!();
  }
};

const acquireSlot = async (): Promise<void> => {
  if (inFlight < MAX_CONCURRENT) {
    inFlight++;
  } else {
    // pump() has already claimed the slot on our behalf before waking us.
    await new Promise<void>(resolve => waiting.push(resolve));
  }
  const sinceLast = Date.now() - lastStartedAt;
  if (sinceLast < MIN_SPACING_MS) {
    await new Promise(resolve => setTimeout(resolve, MIN_SPACING_MS - sinceLast));
  }
  lastStartedAt = Date.now();
};

const releaseSlot = () => {
  inFlight--;
  pump();
};

// Queueing alone would remove the one virtue of the old limiter: when the quota
// really is spent, it stopped asking. Google answers 429 for the whole IP, so
// once one request is refused every other is too — carrying on just trades a
// missing cover for a missing cover plus a console error. Back off for a
// spell, then let one through and see.
const RATE_LIMIT_COOLDOWN_MS = 60_000;
let rateLimitedUntil = 0;

const isRateLimited = () => Date.now() < rateLimitedUntil;
const noteRateLimited = () => {
  rateLimitedUntil = Date.now() + RATE_LIMIT_COOLDOWN_MS;
  console.warn(
    `Google Books returned 429; pausing lookups for ${RATE_LIMIT_COOLDOWN_MS / 1000}s. ` +
    `Covers already stored are unaffected.`
  );
};

export const searchGoogleBooks = async (query: string, maxResults = 10): Promise<GoogleBook[]> => {
  if (!query.trim()) return [];
  
  const cacheKey = `search:${query}:${maxResults}`;
  const cached = googleBooksCache.get(cacheKey);
  if (cached) {
    console.log(`Cache hit for query: ${query}`);
    return cached;
  }
  
  if (isRateLimited()) return [];

  await acquireSlot();

  // Re-check: a request that queued behind others may find the quota spent by
  // the time it reaches the front.
  if (isRateLimited()) {
    releaseSlot();
    return [];
  }

  try {
    console.log(`Google Books API request: ${query} (max: ${maxResults})`);

    const fetchVolumes = async (q: string): Promise<GoogleBooksApiResponse | null> => {
      let data: GoogleBooksApiResponse | null = null;

      // Primary: our keyed proxy. Race against a timeout so a slow function never
      // hangs a cover (the background call is harmless — the slot is released in
      // finally either way).
      const proxyRes = (await Promise.race([
        supabase.functions.invoke('google-books-search', {
          body: {
            q,
            maxResults: Math.min(maxResults, 40),
            printType: 'books',
            langRestrict: 'en',
            orderBy: 'relevance',
          },
        }),
        new Promise((resolve) =>
          setTimeout(() => resolve({ data: null, error: { name: 'TimeoutError' } }), REQUEST_TIMEOUT)
        ),
      ])) as { data: GoogleBooksApiResponse | null; error: unknown };

      if (!proxyRes.error && proxyRes.data) {
        data = proxyRes.data;
      } else {
        // Proxy unavailable/unkeyed/limited — fall back to a direct anonymous call
        // (the previous behaviour) so covers never regress. Once the edge function
        // is deployed with GOOGLE_BOOKS_API_KEY set, the proxy succeeds and this
        // path is never reached.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
        try {
          const params = new URLSearchParams({
            q,
            maxResults: Math.min(maxResults, 40).toString(),
            printType: 'books',
            langRestrict: 'en',
            orderBy: 'relevance',
          });
          const response = await fetch(`${BASE_URL}?${params}`, {
            signal: controller.signal,
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) {
            if (response.status === 429) noteRateLimited();
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          data = await response.json();
        } finally {
          clearTimeout(timeoutId);
        }
      }
      return data;
    };

    const toBooks = (data: GoogleBooksApiResponse | null): GoogleBook[] =>
      ((data?.items || []).map(transformGoogleBookData).filter(Boolean) as GoogleBook[]);

    let books = toBooks(await fetchVolumes(query));

    // A title carrying an editorial aside — "Vivarium (short story 'The New
    // Neighbours')" — over-specifies the search: every word in the parentheses
    // is another term Google has to satisfy, so it answers 200 with zero items
    // and the book silently gets no cover. Retry once on the bare title. This
    // runs ONLY when the caller's own query found nothing, so a parenthetical
    // that genuinely helps is never overridden.
    if (books.length === 0 && isWorthSimplifying(query)) {
      const simplified = simplifyBookQuery(query);
      console.log(`No matches for "${query}" — retrying as "${simplified}"`);
      books = toBooks(await fetchVolumes(simplified));
    }

    if (!books) return [];

    console.log(`Google Books API response: ${books.length} books found for "${query}"`);
    books.forEach(book => {
      console.log(`- ${book.title} by ${book.author} | Cover: ${book.coverUrl ? 'Yes' : 'No'}`);
    });
    
    googleBooksCache.set(cacheKey, books);
    return books;
    
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn(`Google Books API request timed out for: ${query}`);
    } else {
      console.error(`Error searching Google Books for "${query}":`, error);
    }
    return [];
  } finally {
    // Must run on every path, or the queue stalls behind a leaked slot.
    releaseSlot();
  }
};

export const getBookByISBN = async (isbn: string): Promise<GoogleBook | null> => {
  const cacheKey = `isbn:${isbn}`;
  const cached = googleBooksCache.get(cacheKey);
  if (cached && cached.length > 0) return cached[0];
  
  try {
    console.log(`Searching Google Books by ISBN: ${isbn}`);
    const books = await searchGoogleBooks(`isbn:${isbn}`, 1);
    return books[0] || null;
  } catch (error) {
    console.error(`Error fetching book by ISBN ${isbn}:`, error);
    return null;
  }
};
